
// Valores de referência e funções utilitárias
const femaleHormoneReferences = {
  estradiol: { min: 46, max: 607, unit: "pg/mL" },
  fsh: { min: 2.8, max: 12.0, unit: "mUI/mL" },
  lh: { min: 1.8, max: 11.8, unit: "mUI/mL" },
  progesterona: { min: 0.2, max: 28, unit: "ng/mL" },
  amh: { min: 1.0, max: 4.0, unit: "ng/mL" },
  prolactina: { min: 3, max: 30, unit: "ng/mL" },
  shbg: { min: 20, max: 130, unit: "nmol/L" },
  cortisol: { min: 6.2, max: 19.4, unit: "μg/dL" }
};

const maleHormoneReferences = {
  testosterona: { min: 240, max: 950, unit: "ng/dL" },
  estradiol: { min: 10, max: 40, unit: "pg/mL" },
  lh: { min: 1.5, max: 9.3, unit: "mUI/mL" },
  fsh: { min: 1.6, max: 8.0, unit: "mUI/mL" },
  prolactina: { min: 2, max: 18, unit: "ng/mL" },
  shbg: { min: 10, max: 57, unit: "nmol/L" },
  cortisol: { min: 6.2, max: 19.4, unit: "μg/dL" },
  dht: { min: 30, max: 85, unit: "ng/dL" }
};

const metabolicReferences = {
  glicemia: { min: 70, max: 99, unit: "mg/dL" },
  insulina: { min: 2.6, max: 24.9, unit: "μU/mL" },
  ldl: { min: 0, max: 130, unit: "mg/dL" },
  hdl: { min: 40, max: 60, unit: "mg/dL" },
  triglicerides: { min: 0, max: 150, unit: "mg/dL" },
  hematocrito: { min: 37, max: 47, unit: "%" }
};

// Função para obter referência hormonal
function getHormoneReference(hormone, gender) {
  const references = gender === "Feminino" ? femaleHormoneReferences : maleHormoneReferences;
  return references[hormone.toLowerCase()] || { min: 0, max: 0, unit: "" };
}

// Função para determinar status do hormônio
function getHormoneStatus(value, reference) {
  if (!reference || !value) return "normal";
  if (value < reference.min) return "low";
  if (value > reference.max) return "high";
  return "normal";
}

// Função para obter cor baseada no status
function getStatusColor(status) {
  switch (status) {
    case "low": return "#ef4444"; // vermelho
    case "high": return "#f59e0b"; // amarelo
    default: return "#4ade80"; // verde
  }
}

// Função para analisar hormônios femininos
function analyzeFemaleHormones(values, age) {
  const results = {
    status: determineStatus(values, age),
    correlations: [],
    recommendations: [],
    references: []
  };

  // Análise FSH/LH
  const fshLhRatio = values.fsh && values.lh ? values.fsh / values.lh : null;
  if (fshLhRatio !== null) {
    results.correlations.push({
      title: "Relação FSH/LH",
      value: fshLhRatio.toFixed(2),
      status: analyzeFSHLHRatio(fshLhRatio, age),
      description: getFSHLHDescription(fshLhRatio, age)
    });
  }

  // Análise individual dos hormônios
  Object.entries(values).forEach(([hormone, value]) => {
    const reference = getHormoneReference(hormone, "Feminino");
    const status = getHormoneStatus(value, reference);
    
    results.correlations.push({
      title: hormone.toUpperCase(),
      value: `${value} ${reference.unit}`,
      status: status,
      description: getHormoneDescription(hormone, status, age)
    });
  });

  // Adicionar recomendações baseadas na idade e valores
  results.recommendations = generateRecommendations(values, age);
  
  // Adicionar referências
  results.references = [
    "Clínica Fecondare. Qual é a taxa de hormônio ideal para engravidar?",
    "Ortopedista do Joelho. FSH, LH, estrogênio e progesterona.",
    "Laborclínica. Testes de avaliação hormonal da mulher em idade reprodutiva.",
    "Elsevier. Importância do hormônio anti-Mülleriano na infertilidade."
  ];

  return results;
}

// Função para analisar hormônios masculinos
function analyzeMaleHormones(values) {
  const results = {
    correlations: [],
    recommendations: [],
    severity: "low",
    references: []
  };

  Object.entries(values).forEach(([hormone, value]) => {
    const reference = getHormoneReference(hormone, "Masculino");
    const status = getHormoneStatus(value, reference);
    
    results.correlations.push({
      title: hormone.toUpperCase(),
      value: `${value} ${reference.unit}`,
      status: status,
      description: getMaleHormoneDescription(hormone, status)
    });
  });

  // Adicionar recomendações
  results.recommendations = generateMaleRecommendations(values);

  return results;
}

// Função principal de análise hormonal
function analyzeHormonalAxis(examValues, gender) {
  return gender === "Feminino" 
    ? analyzeFemaleHormones(examValues, examValues.age) 
    : analyzeMaleHormones(examValues);
}

// Funções auxiliares
function determineStatus(values, age) {
  const { fsh, estradiol, amh } = values;
  
  if (age < 40 && fsh > 30 && estradiol < 20) {
    return "Menopausa Precoce";
  }
  if (age > 55 && fsh < 30) {
    return "Menopausa Tardia";
  }
  if (fsh > 30 && estradiol < 20 && amh < 0.5) {
    return "Pós-Menopausa";
  }
  if (fsh > 12 && fsh < 30) {
    return "Perimenopausa";
  }
  return "Pré-Menopausa";
}

function analyzeFSHLHRatio(ratio, age) {
  if (age > 45 && ratio > 3) return "high";
  if (ratio < 0.5) return "low";
  return "normal";
}

function getFSHLHDescription(ratio, age) {
  if (age > 45 && ratio > 3) {
    return "Padrão sugestivo de transição menopausal";
  }
  if (ratio < 0.5) {
    return "Possível desequilíbrio hormonal";
  }
  return "Relação dentro dos parâmetros esperados";
}

function getHormoneDescription(hormone, status, age) {
  const descriptions = {
    fsh: {
      high: age > 45 ? "Sugestivo de transição menopausal" : "Elevado para a idade",
      low: "Possível supressão do eixo hipotálamo-hipofisário",
      normal: "Dentro dos valores de referência"
    },
    estradiol: {
      high: "Níveis elevados podem indicar hiperestrogenismo",
      low: "Níveis baixos podem causar sintomas de hipoestrogenismo",
      normal: "Níveis adequados para a idade"
    }
    // Adicione mais hormônios conforme necessário
  };

  return descriptions[hormone.toLowerCase()]?.[status] || "Valor requer avaliação médica";
}

function generateRecommendations(values, age) {
  const recommendations = [];
  const status = determineStatus(values, age);

  switch (status) {
    case "Menopausa Precoce":
      recommendations.push(
        "Avaliação endocrinológica urgente",
        "Investigação de causas secundárias",
        "Considerar terapia hormonal",
        "Avaliação de densidade óssea",
        "Acompanhamento cardiovascular"
      );
      break;
    case "Pós-Menopausa":
      recommendations.push(
        "Avaliação de risco cardiovascular",
        "Densitometria óssea",
        "Considerar terapia hormonal se indicado",
        "Monitoramento de saúde óssea"
      );
      break;
    // Adicione mais casos conforme necessário
  }

  return recommendations;
}

function getMaleHormoneDescription(hormone, status) {
  const descriptions = {
    testosterona: {
      high: "Níveis elevados de testosterona",
      low: "Possível hipogonadismo",
      normal: "Níveis adequados"
    }
    // Adicione mais hormônios conforme necessário
  };

  return descriptions[hormone.toLowerCase()]?.[status] || "Valor requer avaliação médica";
}

function generateMaleRecommendations(values) {
  const recommendations = [];
  // Adicione lógica para recomendações masculinas
  return recommendations;
}

// Exportações
export {
  analyzeHormonalAxis,
  getHormoneStatus,
  getStatusColor,
  getHormoneReference,
  analyzeFemaleHormones,
  analyzeMaleHormones,
  femaleHormoneReferences,
  maleHormoneReferences,
  metabolicReferences
};
